import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    adicionarCidadeEstabelecimento,
    excluirBairros,
    obterBairros,
    obterCidadesEstabelecimento,
    obterTodasCidades,
    removerCidadeEstabelecimento,
    salvarBairros,
} from "../../../services/atribuirRegiao"
import { getIdEstabelecimentoAtual } from "../../../services/quitacaoAutomatica"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import type { BairroRegiao } from "../models/atribuirRegiaoModel"
import { AtribuirRegiaoInicial } from "../models/atribuirRegiaoModel"
import { AtribuirRegiaoActionsTypes } from "./atribuirRegiaoActions"
import { AtribuirRegiaoContext } from "./AtribuirRegiaoContext"
import { atribuirRegiaoReducer } from "./atribuirRegiaoReducer"

export function AtribuirRegiaoContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(atribuirRegiaoReducer, AtribuirRegiaoInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)
    const configuracoes = useAppStore(s => s.configuracoes)
    // Loja SELECIONADA no seletor (estabelecimentoAtualCF); o parametros.estabelecimento
    // aponta para a loja do usuario logado e nao reflete a troca de loja.
    const idEstabelecimento =
        getIdEstabelecimentoAtual() || configuracoes?.user?.parametros?.estabelecimento?.id

    const carregar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !idEstabelecimento) return

        try {
            setLoading(true)
            const [resBairros, resCidades, resTodasCidades] = await Promise.all([
                obterBairros(autorizacao, idEstabelecimento),
                obterCidadesEstabelecimento(autorizacao, idEstabelecimento),
                obterTodasCidades(autorizacao),
            ])

            if ("retornoErro" in resBairros) {
                setErroCarregamento(resBairros.mensagem || "Erro ao carregar bairros")
            } else {
                setErroCarregamento(null)
                dispatch({ type: AtribuirRegiaoActionsTypes.CARREGAR_BAIRROS, payload: resBairros })
            }
            if (!("retornoErro" in resCidades)) {
                dispatch({
                    type: AtribuirRegiaoActionsTypes.CARREGAR_CIDADES_ESTABELECIMENTO,
                    payload: resCidades,
                })
            }
            if (!("retornoErro" in resTodasCidades)) {
                dispatch({
                    type: AtribuirRegiaoActionsTypes.CARREGAR_TODAS_CIDADES,
                    payload: resTodasCidades,
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar bairros")
        } finally {
            setLoading(false)
        }
    }, [idEstabelecimento])

    useEffect(() => {
        carregar()
    }, [carregar])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !idEstabelecimento) return

        setLoading(true)
        try {
            const response = await salvarBairros(autorizacao, idEstabelecimento, dados.bairros)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({ type: AtribuirRegiaoActionsTypes.CARREGAR_BAIRROS, payload: response })
        } finally {
            setLoading(false)
        }
    }, [dados.bairros, idEstabelecimento])

    const removerBairro = useCallback(
        async (index: number, bairro: BairroRegiao) => {
            if (bairro.id !== null && bairro.id > 0) {
                const autorizacao = localStorage.getItem("chaveSistema")
                if (!autorizacao || !idEstabelecimento) return

                setLoading(true)
                try {
                    const response = await excluirBairros(autorizacao, idEstabelecimento, [bairro])
                    if ("retornoErro" in response) {
                        throw new Error(response.mensagem || "Erro ao remover bairro")
                    }
                    dispatch({
                        type: AtribuirRegiaoActionsTypes.CARREGAR_BAIRROS,
                        payload: response,
                    })
                    return
                } finally {
                    setLoading(false)
                }
            }
            dispatch({
                type: AtribuirRegiaoActionsTypes.REMOVER_BAIRRO,
                payload: { index },
            })
        },
        [idEstabelecimento]
    )

    const adicionarCidade = useCallback(
        async (idCidade: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao || !idEstabelecimento) return

            setLoading(true)
            try {
                const response = await adicionarCidadeEstabelecimento(
                    autorizacao,
                    idEstabelecimento,
                    idCidade
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao adicionar cidade")
                }
                dispatch({
                    type: AtribuirRegiaoActionsTypes.CARREGAR_CIDADES_ESTABELECIMENTO,
                    payload: response,
                })
            } finally {
                setLoading(false)
            }
        },
        [idEstabelecimento]
    )

    const removerCidade = useCallback(
        async (idCidade: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao || !idEstabelecimento) return

            setLoading(true)
            try {
                const response = await removerCidadeEstabelecimento(
                    autorizacao,
                    idEstabelecimento,
                    idCidade
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao remover cidade")
                }
                dispatch({
                    type: AtribuirRegiaoActionsTypes.CARREGAR_CIDADES_ESTABELECIMENTO,
                    payload: response,
                })
            } finally {
                setLoading(false)
            }
        },
        [idEstabelecimento]
    )

    return (
        <AtribuirRegiaoContext.Provider
            value={{
                dados,
                dispatch,
                salvar,
                removerBairro,
                adicionarCidade,
                removerCidade,
                erroCarregamento,
            }}
        >
            <Loading />
            {children}
        </AtribuirRegiaoContext.Provider>
    )
}
